import { useCallback } from 'react';
import type { ComponentProps } from 'react';

import type { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';

import { AboveTheFoldReactSerializer } from './AboveTheFoldReactSerializer.tsx';
import { ProgressiveReactSerializer } from './ProgressiveReactSerializer.tsx';
import { useOnlySerializeFirstBatch } from './useOnlySerializeFirstBatch.ts';
import { useProgressiveRenderingConfig } from './useProgressiveRenderingConfig.ts';

type UseCreateSerializerProps = Parameters<typeof useProgressiveRenderingConfig>[0] & {
	adfContentId?: string;
	onContentComplete?: () => void;
};

type CreateSerializer = ComponentProps<typeof ADFRenderer>['createSerializer'];

export const useCreateSerializer = (props: UseCreateSerializerProps): CreateSerializer => {
	const { adfContentId, onContentComplete, skip } = props;

	const { isComplexPageReason, batchSize } = useProgressiveRenderingConfig(props);

	// Initially, only serialize the first batch. This is a performance optimization to delay
	// serialization of the remaining batches until later. The optimization is disabled if there
	// are not enough batches to warrant it.
	//
	// For SSR, subsequent serializations never happen.
	// For SPA, subsequent serializations happen after the first batch is rendered.
	const { onlySerializeFirstBatch, onBatchRendered } = useOnlySerializeFirstBatch({
		adfContentId,
		onContentComplete,
	});

	const createComplexPageSerializer: NonNullable<CreateSerializer> = useCallback(
		(init) => {
			if (isComplexPageReason === 'TooManyExtensions') {
				return new AboveTheFoldReactSerializer(init, onlySerializeFirstBatch, onBatchRendered);
			} else {
				// Progressively renders ADF as described at https://hello.atlassian.net/wiki/spaces/~lmarinov/pages/5177285037/COMPLEXIT+Progressive+rendering+of+ADF
				return new ProgressiveReactSerializer(
					init,
					batchSize,
					onlySerializeFirstBatch,
					onBatchRendered,
				);
			}
		},
		[batchSize, isComplexPageReason, onlySerializeFirstBatch, onBatchRendered],
	);

	if (!skip && isComplexPageReason) {
		return createComplexPageSerializer;
	}
};
